import { useMemo } from 'react'
import { Music } from 'lucide-react'
import { fbt } from 'fbtee'
import { MemberFacepile } from '../../components/MemberFacepile'
import { EventInterestIcon } from '../../components/EventInterestIcon'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { clickableProps } from '../../lib/a11y'
import { cn } from '../../lib/utils'
import type { CrewPick } from '../../lib/crewTopPicks'
import type { UserProfile } from '../../types'

export type CrewPickStatus = 'none' | 'interested' | 'going'

export function CrewPickRow({
  pick,
  name,
  imageUrls,
  when,
  status,
  profiles,
  onSelect,
  onToggle,
}: {
  pick: CrewPick
  name: string
  /** The set card's photos (each b2b artist's), or the lineup artist's. */
  imageUrls: string[]
  /** "Sat 22:00" for a set; nothing for a lineup artist. */
  when?: string
  status: CrewPickStatus
  profiles: Record<string, UserProfile>
  onSelect?: () => void
  onToggle: () => void
}) {
  const going = pick.goingUserIds.length
  const interested = pick.userIds.length - going
  const interestedIds = useMemo(() => {
    const goingSet = new Set(pick.goingUserIds)
    return pick.userIds.filter(uid => !goingSet.has(uid))
  }, [pick.userIds, pick.goingUserIds])
  const counts = [
    going > 0 && fbt(fbt.plural('going', going, { showCount: 'yes', many: 'going' }), 'Crew top picks row: how many of the viewer\'s crew are going to this set'),
    interested > 0 && fbt(fbt.plural('interested', interested, { showCount: 'yes', many: 'interested' }), 'Crew top picks row: how many of the viewer\'s crew are interested in this set or artist'),
  ].filter(Boolean)

  return (
    <li
      {...clickableProps(onSelect)}
      data-testid="crew-pick-row"
      className={cn('flex items-center gap-3', onSelect && 'cursor-pointer')}
    >
      <ThumbStack srcs={imageUrls} icon={Music} size="md" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="truncate text-xs text-muted-foreground tabular-nums">
          {[when, ...counts].filter(Boolean).join(' · ')}
        </p>
      </div>
      <MemberFacepile
        interestedMemberIds={interestedIds}
        goingMemberIds={pick.goingUserIds}
        profiles={profiles}
        dialogTitle={name}
        historyKey={`crew-pick:${pick.id}`}
        max={3}
      />
      <button
        type="button"
        aria-label={
          status === 'none'
            ? fbt('Mark interested', 'Accessible label for the heart button that marks interest on an interest card')
            : fbt('Change interest', 'Accessible label for the heart button on a crew top picks row the viewer already marked')
        }
        onClick={e => {
          e.stopPropagation()
          onToggle()
        }}
        className="-m-1 shrink-0 cursor-pointer p-1 text-muted-foreground"
      >
        <EventInterestIcon status={status} className="size-5" />
      </button>
    </li>
  )
}
